import { generateId, toggleChildNodeAvailability } from "shared/functions";
import { EventListener, KeyCodes, mustBeHTMLElement } from "silver";

export enum ContextualPopupOrientation {
	ABOVE = "above",
	BELOW = "below",
	LEFT = "left",
	RIGHT = "right"
}

export enum ContextualPopupAlignment {
	START = "start",
	CENTER = "center",
	END = "end"
}

export enum ContextualPopupBehavior {
	NONE,
	HOVER,
	CLICK
}


function getElementSize(element: HTMLElement): { width: number, height: number } {
	const clone = mustBeHTMLElement(element.cloneNode(true));
	clone.style.visibility = "hidden";
	clone.style.position = "absolute";
	clone.style.display = "block";
	clone.style.height = "";
	clone.style.width = "";
	clone.style.transform = "none";
	document.body.appendChild(clone);
	const rect = clone.getBoundingClientRect();
	const size = {
		width:  rect.width,
		height: rect.height
	};
	document.body.removeChild(clone);
	return size;
}

function asClass(enumValue: ContextualPopupOrientation|ContextualPopupAlignment): string {
	return `-${enumValue}`;
}

function getViewportWidth(windowObject: Window): number {
	return Math.max(windowObject.document.documentElement.clientWidth, windowObject.innerWidth || 0);
}

function getRelativeAlignment(target: { width: number, height: number }, context: HTMLElement, orientation: ContextualPopupOrientation): ContextualPopupAlignment {
	if (orientation === ContextualPopupOrientation.RIGHT || orientation === ContextualPopupOrientation.LEFT) {
		return ContextualPopupAlignment.CENTER;
	}
	const contextRect   = context.getBoundingClientRect();
	const viewportWidth = getViewportWidth(window);
	const center = (contextRect.width / 2) + contextRect.left;
	const half = target.width / 2;
	if (center - half < 0) {
		return ContextualPopupAlignment.START;
	}
	if (center + half > viewportWidth) {
		return ContextualPopupAlignment.END;
	}
	return ContextualPopupAlignment.CENTER;
}

function getRelativeOrientation(target: { width: number, height: number }, context: HTMLElement, verticalOnly: boolean = false): ContextualPopupOrientation {
	const contextRect   = context.getBoundingClientRect();
	const viewportWidth = getViewportWidth(window);

	if (!verticalOnly && contextRect.left + (contextRect.width - target.width) / 2 <= 0) {
		return ContextualPopupOrientation.RIGHT;
	}
	if (!verticalOnly && contextRect.left + (contextRect.width + target.width) / 2 >= viewportWidth) {
		return ContextualPopupOrientation.LEFT;
	}
	if (contextRect.top - target.height < 0) {
		return ContextualPopupOrientation.BELOW;
	}
	return ContextualPopupOrientation.ABOVE;
}

function getRelativePosition(
	target: { width: number, height: number },
	context: { width: number, height: number, left: number, top: number },
	orientation: ContextualPopupOrientation,
	alignment: ContextualPopupAlignment
): { left: number, top: number } {
	if (orientation === ContextualPopupOrientation.RIGHT) {
		return {
			left: context.left + context.width,
			top:  context.top + (context.height - target.height) / 2
		};
	}

	if (orientation === ContextualPopupOrientation.LEFT) {
		return {
			left: context.left - target.width,
			top:  context.top + (context.height - target.height) / 2
		};
	}

	const multiplier = alignment === ContextualPopupAlignment.CENTER ? 0.5 : alignment === ContextualPopupAlignment.START ? 0 : 1;
	if (orientation === ContextualPopupOrientation.BELOW) {
		return {
			left: context.left + multiplier * (context.width - target.width),
			top:  context.top + context.height
		};
	}

	return {
		left: context.left + multiplier * (context.width - target.width),
		top:  context.top - target.height
	};
}

export class ContextualPopup {
	public readonly onHide = EventListener.create<{ sender: ContextualPopup }>("dismiss");
	public readonly onShow = EventListener.create<{ sender: ContextualPopup }>("show");

	public    readonly element = document.createElement("div");
	protected readonly contents = document.createElement("div");
	private openValue: boolean = false;
	private animateTimeout: number = -1;
	public verticalOnly: boolean = false;
	public horizontalAttachment: "left"|"right" = "left";
	protected context: HTMLElement | null = null;
	protected restoreFocus: HTMLElement | null = null;
	private showTimerHandle: number = 0;
	protected unbindOnHide: Array<() => void> = [];
	protected hoverDelay: number = 0;

	public forcedOrientation?: ContextualPopupOrientation;
	public forcedAlignment?: ContextualPopupAlignment;

	constructor(
		protected readonly content: Node | string,
		modifierClass?: string
	) {
		this.contents.id = generateId("popup-");
		this.contents.classList.add("info_contents");
		this.contents.append(content);

		this.element.classList.add("info", "-hidden");
		this.element.appendChild(this.contents);

		if (modifierClass) {
			this.element.classList.add(modifierClass);
		}
	}

	/**
	 * Fixes the context menu to the viewport, instead of relative to the shared parent container.
	 *
	 * @param fixed
	 */
	public set fixed(fixed: boolean) {
		this.element.classList.toggle("-fixed", fixed);
	}

	public get fixed(): boolean {
		return this.element.classList.contains("-fixed");
	}

	public setContext(context: HTMLElement, behavior: ContextualPopupBehavior): void {
		if (this.context) {
			throw new Error("Reference already set");
		}

		context.setAttribute("aria-describedby", this.contents.id);

		this.context = context;
		this.clearEvents();
		if (this.context.tabIndex === -1) {
			this.context.tabIndex = 0;
		}
		this.injectAfterRef();

		if (behavior === ContextualPopupBehavior.HOVER) {
			this.context.addEventListener("mouseover", () => this.showOnDelay());
			this.context.addEventListener("mouseout", () => this.hide());
			this.context.addEventListener("focus", () => this.show());
		} else if (behavior === ContextualPopupBehavior.CLICK) {
			this.context.addEventListener("click", () => this.toggle());
		}

		if (behavior !== ContextualPopupBehavior.NONE) {
			this.context.addEventListener("touchstart", () => this.show());
			this.context.addEventListener("touchend", () => this.hide());
			this.context.addEventListener("keyup", ({ keyCode }) => {
				if (keyCode === KeyCodes.ESC) {
					this.hide();
				}
			});
		}
	}

	public remove(): void {
		const parent = this.element.parentNode;
		if (!parent) {
			return;
		}
		parent.removeChild(this.element);
	}

	private injectAfterRef(): void {
		if (!this.context || !this.context.parentNode) {
			return;
		}
		const parent = this.context.parentNode;
		if (!this.context.nextElementSibling) {
			parent.appendChild(this.element);
		} else if (this.context.nextElementSibling != this.element) {
			parent.insertBefore(this.element, this.context.nextElementSibling);
		}
	}

	public reposition(): void {
		if (!this.context || !this.context.offsetParent || !this.open) {
			return;
		}

		try {
			const targetSize = getElementSize(this.element);
			const orientation = this.forcedOrientation || getRelativeOrientation(targetSize, this.context, this.verticalOnly);
			const alignment = this.forcedAlignment || getRelativeAlignment(targetSize, this.context, orientation);

			const rect = this.context.getBoundingClientRect();
			const context = { width: rect.width, height: rect.height, left: rect.x, top: rect.y };
			if (!this.fixed) {
				context.left = this.context.offsetLeft;
				context.top = this.context.offsetTop;
			}

			const pos = getRelativePosition(targetSize, context, orientation, alignment);

			this.element.classList.remove(
				asClass(ContextualPopupOrientation.LEFT),
				asClass(ContextualPopupOrientation.BELOW),
				asClass(ContextualPopupOrientation.RIGHT),
				asClass(ContextualPopupOrientation.ABOVE),
				asClass(ContextualPopupAlignment.END),
				asClass(ContextualPopupAlignment.CENTER),
				asClass(ContextualPopupAlignment.START)
			);
			this.element.classList.add(asClass(orientation), asClass(alignment));

			this.element.style.top = `${pos.top}px`;
			if (this.horizontalAttachment === "left") {
				this.element.style.left = `${pos.left}px`;
				this.element.style.right = "";
			} else if (this.horizontalAttachment === "right") {
				const right = mustBeHTMLElement(this.context.offsetParent).offsetWidth - (pos.left + targetSize.width);
				this.element.style.right = `${right}px`;
				this.element.style.left = "";
			}
		} catch (err) {
			console.warn(err);
		}
	}

	public get open(): boolean {
		return this.openValue;
	}

	protected bindAndAutoUnbind(target: EventTarget, event: string, handler: (...args: any[]) => any): void {
		target.addEventListener(event, handler);
		this.unbindOnHide.push(() => target.removeEventListener(event, handler));
	}

	protected clearEvents(): void {
		this.unbindOnHide.forEach(unbind => unbind());
		this.unbindOnHide = [];
	}

	protected isInternalNode(node: any): node is Node {
		return (node instanceof Node) && (this.element.contains(node) || (this.context !== null && this.context.contains(node)));
	}

	private showOnDelay(): void {
		clearTimeout(this.showTimerHandle);
		this.showTimerHandle = setTimeout(() => {
			this.show();
		}, this.hoverDelay);
	}

	public toggle(value: boolean = !this.open): void {
		if (value) {
			this.show();
		} else {
			this.hide();
		}
	}

	public show(allowScroll: boolean = false): void {
		clearTimeout(this.showTimerHandle);
		if (!this.context) {
			throw new Error("No associated context");
		}
		if (this.openValue) {
			return;
		}

		const dismissOnExternalEvent = (e: Event): void => {
			if (!this.isInternalNode(e.target)) {
				this.hide();
			}
		};
		const dismissOnFocusOut = (e: FocusEvent): void => {
			if (!this.isInternalNode(e.relatedTarget)) {
				this.restoreFocus = null;
				this.hide();
			}
		};

		if (!allowScroll) {
			this.bindAndAutoUnbind(document.body, "mousewheel", dismissOnExternalEvent);
			this.bindAndAutoUnbind(document.body, "DOMMouseScroll", dismissOnExternalEvent);
			this.bindAndAutoUnbind(document.body, "touchstart", dismissOnExternalEvent);
		} else {
			this.bindAndAutoUnbind(document.body, "touchend", dismissOnExternalEvent);
		}
		this.bindAndAutoUnbind(document.body, "mousedown", dismissOnExternalEvent);
		this.bindAndAutoUnbind(this.context, "focusout", dismissOnFocusOut);
		this.bindAndAutoUnbind(this.element, "focusout", dismissOnFocusOut);
		this.bindAndAutoUnbind(window, "resize", () => this.hide());

		this.openValue = true;
		this.updateHiddenClass().catch(console.error);
		this.reposition();
		this.updateChildNodeAvailability();
		this.injectAfterRef();

		if (!this.isInternalNode(document.activeElement)) {
			this.restoreFocus = document.activeElement;
		}

		this.onShow.trigger({ sender: this });
	}

	public async hide(): Promise<void> {
		if (this.restoreFocus) {
			this.restoreFocus.focus();
			this.restoreFocus = null;
		}
		clearTimeout(this.showTimerHandle);
		if (!this.openValue) {
			return;
		}
		this.clearEvents();
		this.openValue = false;
		this.updateChildNodeAvailability();
		await this.updateHiddenClass();

		this.onHide.trigger({ sender: this });
	}

	private updateHiddenClass(): Promise<void> {
		clearTimeout(this.animateTimeout);
		this.element.classList.add("-animate");
		return new Promise(resolve => {
			setTimeout(() => {
				this.element.classList.toggle("-hidden", !this.openValue);
				this.animateTimeout = setTimeout(() => {
					this.element.classList.remove("-animate");
					resolve();
				}, 400);
			}, 1);
		});
	}

	protected updateChildNodeAvailability(): void {
		toggleChildNodeAvailability(this.element, this.openValue);
	}

	public static makeFromHTML(context: HTMLElement, html: string, trimAfter?: number): ContextualPopup {
		if (trimAfter && html.length > trimAfter) {
			html = html.substring(0, trimAfter).replace(/(\s+[^\s]+)$/, "…");
		}
		const element = document.createElement("span");
		element.innerHTML = html;
		const popup = new ContextualPopup(element, "-tooltip");
		popup.setContext(context, ContextualPopupBehavior.HOVER);
		return popup;
	}

	public static makeFromTitleAndText(title: string | Node, text: string | Node, rootClass?: string, modifierClass?: string): ContextualPopup {
		const element = document.createElement("div");
		const titleEl = document.createElement("h2");
		const textEl  = document.createElement("p");
		const iconEl  = document.createElement("div");

		iconEl.classList.add("announcement_icon");
		element.classList.add("announcement", "-tooltip");
		if (rootClass) {
			element.classList.add(rootClass);
		}
		titleEl.classList.add("announcement_title");
		textEl.classList.add("announcement_body");
		titleEl.append(title);
		textEl.append(text);
		element.append(titleEl, textEl, iconEl);

		return new ContextualPopup(element, modifierClass);
	}
}
